import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMechanicAuth } from '../MechanicAuthContext';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Plus, FileText, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MechanicInspectionsPage() {
  const { token } = useMechanicAuth();
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showOrderPicker, setShowOrderPicker] = useState(null);
  const [processing, setProcessing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [inspRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanic/inspections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/mechanic/work-orders?status=open`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInspections(inspRes.data || []);
      setOpenOrders(ordersRes.data || []);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateOrder = async (inspId) => {
    setProcessing(inspId);
    try {
      const res = await axios.post(
        `${API_URL}/api/mechanic/inspections/${inspId}/create-work-order`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Bon ${res.data.order_number} cree`);
      navigate(`/admin/mecanicien/bons/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleAddToOrder = async (inspId, orderId) => {
    setProcessing(inspId);
    try {
      await axios.post(
        `${API_URL}/api/mechanic/inspections/${inspId}/add-to-order/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Defauts ajoutes au bon');
      setShowOrderPicker(null);
      navigate(`/admin/mecanicien/bons/${orderId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkReviewed = async (inspId) => {
    setProcessing(inspId);
    try {
      await axios.post(
        `${API_URL}/api/mechanic/inspections/${inspId}/review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inspection marquee comme revue');
      setInspections(prev => prev.filter(i => i.id !== inspId));
    } catch {
      toast.error('Erreur');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const unreviewedInspections = inspections.filter(i => !i.mechanic_reviewed);
  const reviewedInspections = inspections.filter(i => i.mechanic_reviewed);

  return (
    <div className="p-4 pb-20 space-y-4" data-testid="mechanic-inspections-page">
      <h1 className="text-xl font-bold text-white">Inspections</h1>
      <p className="text-slate-400 text-sm">Inspections avec defauts signales par les chauffeurs</p>

      {unreviewedInspections.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center mt-4">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500/50 mb-3" />
          <p className="text-slate-400 text-sm">Aucune inspection en attente</p>
          <p className="text-slate-600 text-xs mt-1">Tout est en ordre</p>
        </div>
      ) : (
        <div className="space-y-3">
          {unreviewedInspections.map(insp => {
            const isExpanded = expanded === insp.id;
            const defects = insp.defects || [];
            const isProcessing = processing === insp.id;

            return (
              <div key={insp.id} className="bg-slate-900 border border-red-500/20 rounded-xl overflow-hidden" data-testid={`inspection-${insp.id}`}>
                {/* Header */}
                <div onClick={() => setExpanded(isExpanded ? null : insp.id)} className="p-4 cursor-pointer" role="button" tabIndex={0}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
                    <span className="text-white font-semibold text-sm flex-1 truncate">
                      {insp.vehicle_name || insp.vehicle_plate || insp.vehicle_unit || 'Vehicule'}
                    </span>
                    <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-medium">
                      {defects.length} defaut{defects.length > 1 ? 's' : ''}
                    </span>
                    {isExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                  </div>
                  <div className="flex gap-3 text-xs text-slate-400">
                    <span>{insp.date || ''}</span>
                    {insp.driver_name && <span>Chauffeur: {insp.driver_name}</span>}
                    {insp.mileage && <span>{insp.mileage} km</span>}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    {/* Defects list */}
                    <div className="space-y-1.5">
                      <p className="text-xs text-red-400 font-medium">Defauts signales:</p>
                      {defects.map((d, j) => (
                        <div key={j} className="bg-red-500/5 border-l-2 border-red-500/40 pl-3 py-1.5">
                          <p className="text-xs text-white">{d.item || d.category || 'Element'}</p>
                          {d.notes && <p className="text-[10px] text-slate-400 mt-0.5">{d.notes}</p>}
                        </div>
                      ))}
                    </div>

                    {/* All items summary */}
                    {(insp.items || []).length > 0 && (
                      <div>
                        <p className="text-xs text-slate-400 font-medium mb-1">
                          Total: {insp.items.length} elements verifies, {insp.items.filter(i => i.status === 'ok').length} OK
                        </p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="space-y-2 pt-2">
                      <button
                        onClick={() => handleCreateOrder(insp.id)}
                        disabled={isProcessing}
                        className="w-full py-2.5 bg-amber-500 text-black font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:bg-amber-400 disabled:opacity-50"
                        data-testid={`create-order-${insp.id}`}
                      >
                        <Plus size={16} /> Creer un bon de travail
                      </button>

                      {openOrders.length > 0 && (
                        <>
                          {showOrderPicker === insp.id ? (
                            <div className="bg-slate-800 rounded-xl p-3 space-y-2">
                              <p className="text-xs text-slate-300 font-medium">Ajouter a un bon existant:</p>
                              {openOrders.map(order => (
                                <button
                                  key={order.id}
                                  onClick={() => handleAddToOrder(insp.id, order.id)}
                                  disabled={isProcessing}
                                  className="w-full p-2.5 bg-slate-900 rounded-lg text-left text-xs text-white active:bg-slate-700 disabled:opacity-50"
                                  data-testid={`add-to-order-${order.id}`}
                                >
                                  <span className="font-medium">{order.order_number}</span>
                                  {order.vehicle_plate && <span className="text-slate-400 ml-2">{order.vehicle_plate}</span>}
                                </button>
                              ))}
                              <button onClick={() => setShowOrderPicker(null)} className="text-xs text-slate-500 w-full text-center py-1">Annuler</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowOrderPicker(insp.id)}
                              className="w-full py-2.5 bg-slate-800 border border-slate-700 text-white font-medium text-sm rounded-xl flex items-center justify-center gap-2 active:bg-slate-700"
                              data-testid={`show-orders-${insp.id}`}
                            >
                              <Wrench size={16} /> Ajouter a un bon existant
                            </button>
                          )}
                        </>
                      )}

                      <button
                        onClick={() => handleMarkReviewed(insp.id)}
                        disabled={isProcessing}
                        className="w-full py-2 text-slate-500 text-xs underline active:text-slate-300 disabled:opacity-50"
                        data-testid={`mark-reviewed-${insp.id}`}
                      >
                        Marquer comme revue (sans bon)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reviewed inspections */}
      {reviewedInspections.length > 0 && (
        <section>
          <h2 className="text-sm font-medium text-slate-500 mb-2 mt-6">Deja revues</h2>
          <div className="space-y-2">
            {reviewedInspections.slice(0, 5).map(insp => (
              <div key={insp.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-3 opacity-60">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-500" />
                  <span className="text-xs text-white">{insp.vehicle_name || insp.vehicle_plate || 'Vehicule'}</span>
                  <span className="text-[10px] text-slate-500 ml-auto">{insp.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
